import React, { useEffect, useMemo, useState } from "react";
import "../style/ManageCars.css";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "../supabase/supabaseClient";

const specOptions = [
  { value: "Vehicle condition", key: "vehicleCondition" },
  { value: "Category", key: "category" },
  { value: "Series", key: "series" },
  { value: "Equipment line", key: "equipmentLine" },
  { value: "Origin", key: "origin" },
  { value: "Mileage", key: "mileage" },
  { value: "Engine displacement", key: "engineDisplacement" },
  { value: "Performance", key: "performance" },
  { value: "Drive type", key: "driveType" },
  { value: "Fuel type", key: "fuelType" },
  { value: "Energy consumption (combined)", key: "energyConsumption" },
  { value: "CO₂ emissions (combined)", key: "co2Emissions" },
  { value: "Fuel consumption", key: "fuelConsumption" },
  { value: "Number of seats", key: "seats" },
  { value: "Number of doors", key: "doors" },
  { value: "Transmission", key: "transmission" },
  { value: "Exterior color", key: "exteriorColor" },
  { value: "Interior color", key: "interiorColor" },
  { value: "Body type", key: "bodyType" },
  { value: "First registration", key: "firstRegistration" },
  { value: "Warranty", key: "warranty" },
  { value: "Vehicle owner", key: "vehicleOwner" },
  { value: "HU", key: "hu" },
  { value: "Air conditioning", key: "airConditioning" },
  { value: "Parking assistance", key: "parkingAssistance" },
  { value: "Airbags", key: "airbags" },
  { value: "Cylinder", key: "cylinder" },
];

const equipmentOptions = [
  { value: "Tinted windows", key: "tintedWindows" },
  { value: "ABS", key: "abs" },
  { value: "Alarm system", key: "alarmSystem" },
  { value: "Detachable towbar", key: "detachableTowbar" },
  { value: "Armrest", key: "armrest" },
  { value: "On-board computer", key: "onBoardComputer" },
  { value: "CD player", key: "cdPlayer" },
  { value: "Electric window regulator", key: "electricWindows" },
  { value: "Electric side mirrors", key: "electricMirrors" },
  { value: "Electric immobiliser", key: "immobiliser" },
  { value: "ESP", key: "esp" },
  { value: "Front-wheel drive", key: "frontWheelDrive" },
  { value: "Luggage compartment partition", key: "luggagePartition" },
  { value: "Isofix", key: "isofix" },
  { value: "Alloy wheels", key: "alloyWheels" },
  { value: "Navigation system", key: "navigation" },
  { value: "Fog lights", key: "fogLights" },
  { value: "Breakdown kit", key: "breakdownKit" },
  { value: "Particle filter", key: "particleFilter" },
  { value: "Sunroof", key: "sunroof" },
  { value: "Power steering", key: "powerSteering" },
  { value: "Heated seats", key: "heatedSeats" },
  { value: "Sound system", key: "soundSystem" },
  { value: "Cruise control", key: "cruiseControl" },
  { value: "Traction control", key: "tractionControl" },
  { value: "Xenon headlights", key: "xenon" },
  { value: "Central locking", key: "centralLocking" },
];

export default function ManageCars() {
  const { t } = useTranslation();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState("");
  const [editingCar, setEditingCar] = useState(null);

  const [editForm, setEditForm] = useState({
    carName: "",
    brand: "",
    year: "",
    price: "",
    status: "Available",
    description: "",
  });

  const [editSpecs, setEditSpecs] = useState([
    { label: "Vehicle condition", value: "" },
  ]);

  const [editEquipment, setEditEquipment] = useState([]);
  const [editImages, setEditImages] = useState([]);
  const [newImageFiles, setNewImageFiles] = useState([]);
  const [newImagePreviews, setNewImagePreviews] = useState([]);

  const usedEditLabels = useMemo(
    () => editSpecs.map((item) => item.label).filter(Boolean),
    [editSpecs]
  );

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  const availableOptionsForEditRow = (currentLabel) =>
    specOptions.filter(
      (option) =>
        option.value === currentLabel || !usedEditLabels.includes(option.value)
    );

  const getStatusLabel = (status) => {
    if (status === "Sold") return t("admin.sold");
    if (status === "Reserved") return t("admin.reserved");
    return t("admin.available");
  };

  const getSpecLabel = (value) => {
    const option = specOptions.find((item) => item.value === value);
    return option ? t(`admin.spec.${option.key}`) : value;
  };

  const getStoragePathFromUrl = (url) => {
    if (!url) return null;

    const marker = "/storage/v1/object/public/car-images/";
    const index = url.indexOf(marker);

    if (index === -1) return null;

    return url.substring(index + marker.length);
  };

  const fetchCars = async () => {
    try {
      setLoading(true);

      const { data, error } = await supabase
        .from("cars")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;

      setCars(data || []);
    } catch (error) {
      console.error("Error fetching cars:", error);
      alert(error.message || t("admin.failedLoad"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCars();
  }, []);

  const uploadCarImages = async (files) => {
    const uploadedUrls = [];

    for (const file of files) {
      const fileExt = file.name.split(".").pop();
      const fileName = `${Date.now()}-${crypto.randomUUID()}.${fileExt}`;
      const filePath = `cars/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from("car-images")
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data } = supabase.storage
        .from("car-images")
        .getPublicUrl(filePath);

      uploadedUrls.push(data.publicUrl);
    }

    return uploadedUrls;
  };

  const handleStatusChange = async (carId, newStatus) => {
    try {
      setSavingId(carId);

      const { error } = await supabase
        .from("cars")
        .update({ status: newStatus })
        .eq("id", carId);

      if (error) throw error;

      setCars((prev) =>
        prev.map((car) =>
          car.id === carId ? { ...car, status: newStatus } : car
        )
      );
    } catch (error) {
      console.error("Error updating status:", error);
      alert(error.message || t("admin.failedStatus"));
    } finally {
      setSavingId("");
    }
  };

 const handleDelete = async (car) => {
  const confirmDelete = window.confirm(t("admin.deleteConfirm"));
  if (!confirmDelete) return;

  try {
    setSavingId(car.id);

    const { data, error } = await supabase.rpc("delete_car_by_id", {
      car_id: car.id,
    });

    if (error) throw error;

    if (data === 0) {
      alert("Car was not deleted. This ID was not found: " + car.id);
      return;
    }

    setCars((prev) => prev.filter((item) => item.id !== car.id));

    alert("Car deleted from Supabase!");
  } catch (error) {
    console.error("Delete error:", error);
    alert(error.message || t("admin.failedDelete"));
  } finally {
    setSavingId("");
  }
};

  const openEditModal = (car) => {
    setEditingCar(car);

    setEditForm({
      carName: car.car_name || "",
      brand: car.brand || "",
      year: car.year || "",
      price: car.price || "",
      status: car.status || "Available",
      description: car.description || "",
    });

    setEditSpecs(
      Array.isArray(car.specifications) && car.specifications.length > 0
        ? car.specifications
        : [{ label: "Vehicle condition", value: "" }]
    );

    setEditEquipment(Array.isArray(car.equipment) ? car.equipment : []);
    setEditImages(Array.isArray(car.images) ? car.images : []);
    setNewImageFiles([]);
    setNewImagePreviews([]);
  };

  const closeEditModal = () => {
    setEditingCar(null);

    setEditForm({
      carName: "",
      brand: "",
      year: "",
      price: "",
      status: "Available",
      description: "",
    });

    setEditSpecs([{ label: "Vehicle condition", value: "" }]);
    setEditEquipment([]);
    setEditImages([]);
    setNewImageFiles([]);
    setNewImagePreviews([]);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;

    setEditForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleEditSpecChange = (index, field, value) => {
    setEditSpecs((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [field]: value } : item))
    );
  };

  const addEditSpecRow = () => {
    setEditSpecs((prev) => [...prev, { label: "", value: "" }]);
  };

  const removeEditSpecRow = (index) => {
    if (editSpecs.length === 1) return;
    setEditSpecs((prev) => prev.filter((_, i) => i !== index));
  };

  const toggleEditEquipment = (item) => {
    setEditEquipment((prev) =>
      prev.includes(item) ? prev.filter((i) => i !== item) : [...prev, item]
    );
  };

  const removeExistingImage = (index) => {
    setEditImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleNewImagesChange = (e) => {
    const files = Array.from(e.target.files);

    if (!files.length) return;

    setNewImageFiles(files);
    setNewImagePreviews(files.map((file) => URL.createObjectURL(file)));
  };

  const removeNewImage = (index) => {
    setNewImageFiles((prev) => prev.filter((_, i) => i !== index));
    setNewImagePreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const saveEdit = async (e) => {
    e.preventDefault();

    if (!editingCar) return;

    try {
      setSavingId(editingCar.id);

      const uploadedNewImages =
        newImageFiles.length > 0 ? await uploadCarImages(newImageFiles) : [];

      const finalImages = [...editImages, ...uploadedNewImages];

      if (finalImages.length === 0) {
        alert(t("admin.carNeedsImage"));
        return;
      }

      const cleanedSpecs = editSpecs.filter(
        (item) => item.label.trim() !== "" && item.value.trim() !== ""
      );

      const technicalData = {};

      cleanedSpecs.forEach((item) => {
        technicalData[item.label] = item.value;
      });

      const updatedData = {
        car_name: editForm.carName.trim(),
        brand: editForm.brand.trim(),
        year: Number(editForm.year),
        price: Number(editForm.price),
        status: editForm.status,
        description: editForm.description.trim(),
        specifications: cleanedSpecs,
        technical_data: technicalData,
        equipment: editEquipment,
        images: finalImages,
        main_image: finalImages[0],
      };

      const { error } = await supabase
        .from("cars")
        .update(updatedData)
        .eq("id", editingCar.id);

      if (error) throw error;

      setCars((prev) =>
        prev.map((car) =>
          car.id === editingCar.id ? { ...car, ...updatedData } : car
        )
      );

      closeEditModal();
    } catch (error) {
      console.error("Error updating car:", error);
      alert(error.message || t("admin.failedUpdate"));
    } finally {
      setSavingId("");
    }
  };

  return (
    <div className="manage-cars-page">
      <button
        type="button"
        className={`manage-hamburger ${sidebarOpen ? "active" : ""}`}
        onClick={() => setSidebarOpen(!sidebarOpen)}
        aria-label="Open admin menu"
      >
        <span></span>
        <span></span>
        <span></span>
      </button>

      {sidebarOpen && (
        <div className="manage-sidebar-overlay" onClick={closeSidebar}></div>
      )}

      <aside className={`manage-sidebar ${sidebarOpen ? "open" : ""}`}>
        <div className="manage-logo-box">
          <div className="manage-logo-icon">🚗</div>

          <div>
            <h3>{t("admin.panel")}</h3>
            <p>{t("admin.brand")}</p>
          </div>
        </div>

        <nav className="manage-nav">
          <Link
            to="/admin/manage-cars"
            className="manage-nav-link active"
            onClick={closeSidebar}
          >
            {t("admin.manageCars")}
          </Link>

          <Link
            to="/admin/add-car"
            className="manage-nav-link"
            onClick={closeSidebar}
          >
            {t("admin.addCar")}
          </Link>
        </nav>

        <Link to="/" className="manage-back-btn" onClick={closeSidebar}>
          {t("admin.backToSite")}
        </Link>
      </aside>

      <main className="manage-content">
        <div className="manage-header">
          <h1>{t("admin.manageCarsTitle")}</h1>
          <p>{t("admin.manageCarsSubtitle")}</p>
        </div>

        {loading ? (
          <div className="manage-empty-box">{t("admin.loadingCars")}</div>
        ) : cars.length === 0 ? (
          <div className="manage-empty-box">{t("admin.noCarsFound")}</div>
        ) : (
          <div className="cars-grid">
            {cars.map((car) => (
              <div className="car-card" key={car.id}>
                <div className="car-image-wrap">
                  {car.main_image ? (
                    <img
                      src={car.main_image}
                      alt={car.car_name}
                      className="car-image"
                    />
                  ) : (
                    <div className="no-image-box">{t("admin.noImage")}</div>
                  )}

                  <span
                    className={`car-badge ${
                      car.status === "Sold"
                        ? "sold"
                        : car.status === "Reserved"
                        ? "reserved"
                        : "available"
                    }`}
                  >
                    {getStatusLabel(car.status)}
                  </span>
                </div>

                <div className="car-card-body">
                  <h2>{car.car_name}</h2>

                  <p className="car-brand">
                    {car.brand} • {car.year}
                  </p>

                  <p className="car-price">
                    € {Number(car.price || 0).toLocaleString()}
                  </p>

                  {car.description && (
                    <p className="car-description">{car.description}</p>
                  )}

                  {Array.isArray(car.specifications) &&
                    car.specifications.length > 0 && (
                      <div className="mini-specs">
                        {car.specifications.slice(0, 3).map((spec, index) => (
                          <div key={index} className="mini-spec-item">
                            <span>{getSpecLabel(spec.label)}</span>
                            <strong>{spec.value}</strong>
                          </div>
                        ))}
                      </div>
                    )}

                  <div className="car-status-row">
                    <label>{t("admin.status")}</label>

                    <select
                      value={car.status || "Available"}
                      onChange={(e) =>
                        handleStatusChange(car.id, e.target.value)
                      }
                      disabled={savingId === car.id}
                    >
                      <option value="Available">{t("admin.available")}</option>
                      <option value="Sold">{t("admin.sold")}</option>
                      <option value="Reserved">{t("admin.reserved")}</option>
                    </select>
                  </div>

                  <div className="car-actions">
                    <button
                      type="button"
                      className="edit-btn"
                      onClick={() => openEditModal(car)}
                    >
                      {t("admin.edit")}
                    </button>

                    <button
                      type="button"
                      className="delete-btn"
                      onClick={() => handleDelete(car)}
                      disabled={savingId === car.id}
                    >
                      {savingId === car.id ? t("admin.saving") : t("admin.delete")}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {editingCar && (
        <div className="edit-modal-overlay">
          <div className="edit-modal">
            <div className="edit-modal-head">
              <h2>{t("admin.editFullCar")}</h2>

              <button
                type="button"
                className="close-modal-btn"
                onClick={closeEditModal}
              >
                ✕
              </button>
            </div>

            <form onSubmit={saveEdit} className="edit-form">
              <div className="edit-form-group">
                <label>{t("admin.carName")}</label>

                <input
                  type="text"
                  name="carName"
                  value={editForm.carName}
                  onChange={handleEditChange}
                  required
                />
              </div>

              <div className="edit-form-group">
                <label>{t("admin.brandLabel")}</label>

                <input
                  type="text"
                  name="brand"
                  value={editForm.brand}
                  onChange={handleEditChange}
                  required
                />
              </div>

              <div className="edit-form-row">
                <div className="edit-form-group">
                  <label>{t("admin.year")}</label>

                  <input
                    type="number"
                    name="year"
                    value={editForm.year}
                    onChange={handleEditChange}
                    required
                  />
                </div>

                <div className="edit-form-group">
                  <label>{t("admin.price")}</label>

                  <input
                    type="number"
                    name="price"
                    value={editForm.price}
                    onChange={handleEditChange}
                    required
                  />
                </div>
              </div>

              <div className="edit-form-group">
                <label>{t("admin.status")}</label>

                <select
                  name="status"
                  value={editForm.status}
                  onChange={handleEditChange}
                >
                  <option value="Available">{t("admin.available")}</option>
                  <option value="Sold">{t("admin.sold")}</option>
                  <option value="Reserved">{t("admin.reserved")}</option>
                </select>
              </div>

              <div className="edit-form-group">
                <label>{t("admin.description")}</label>

                <textarea
                  name="description"
                  value={editForm.description}
                  onChange={handleEditChange}
                  rows="5"
                />
              </div>

              <div className="edit-form-section">
                <div className="section-head">
                  <h3>{t("admin.images")}</h3>
                </div>

                {editImages.length > 0 && (
                  <div className="multi-preview-grid">
                    {editImages.map((image, index) => (
                      <div className="multi-preview-card" key={index}>
                        <img
                          src={image}
                          alt={`Car ${index + 1}`}
                          className="multi-preview-image"
                        />

                        <button
                          type="button"
                          className="remove-image-btn"
                          onClick={() => removeExistingImage(index)}
                        >
                          {t("admin.remove")}
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="edit-form-group">
                  <label>{t("admin.addNewImages")}</label>

                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleNewImagesChange}
                  />
                </div>

                {newImagePreviews.length > 0 && (
                  <div className="multi-preview-grid">
                    {newImagePreviews.map((image, index) => (
                      <div className="multi-preview-card" key={index}>
                        <img
                          src={image}
                          alt={`New preview ${index + 1}`}
                          className="multi-preview-image"
                        />

                        <button
                          type="button"
                          className="remove-image-btn"
                          onClick={() => removeNewImage(index)}
                        >
                          {t("admin.remove")}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="edit-form-section">
                <div className="section-head">
                  <h3>{t("admin.technicalData")}</h3>

                  <button
                    type="button"
                    className="add-spec-btn"
                    onClick={addEditSpecRow}
                  >
                    {t("admin.addStatistic")}
                  </button>
                </div>

                <div className="specs-wrapper">
                  {editSpecs.map((spec, index) => (
                    <div className="spec-row" key={index}>
                      <div className="edit-form-group">
                        <label>{t("admin.statisticName")}</label>

                        <select
                          value={spec.label}
                          onChange={(e) =>
                            handleEditSpecChange(
                              index,
                              "label",
                              e.target.value
                            )
                          }
                        >
                          <option value="">{t("admin.selectStatistic")}</option>

                          {availableOptionsForEditRow(spec.label).map(
                            (option) => (
                              <option key={option.value} value={option.value}>
                                {getSpecLabel(option.value)}
                              </option>
                            )
                          )}
                        </select>
                      </div>

                      <div className="edit-form-group">
                        <label>{t("admin.value")}</label>

                        <input
                          type="text"
                          value={spec.value}
                          onChange={(e) =>
                            handleEditSpecChange(
                              index,
                              "value",
                              e.target.value
                            )
                          }
                          placeholder={t("admin.placeholderValue")}
                        />
                      </div>

                      <button
                        type="button"
                        className="remove-spec-btn"
                        onClick={() => removeEditSpecRow(index)}
                        disabled={editSpecs.length === 1}
                      >
                        {t("admin.remove")}
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="edit-form-section">
                <h3>{t("admin.equipment")}</h3>

                <div className="equipment-grid">
                  {equipmentOptions.map((item) => {
                    const active = editEquipment.includes(item.value);

                    return (
                      <div
                        key={item.value}
                        className={`equipment-item ${active ? "active" : ""}`}
                        onClick={() => toggleEditEquipment(item.value)}
                      >
                        <span>{t(`admin.equipmentItems.${item.key}`)}</span>
                        <span className="check">{active ? "✓" : ""}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              <button
                type="submit"
                className="save-edit-btn"
                disabled={savingId === editingCar.id}
              >
                {savingId === editingCar.id
                  ? t("admin.saving")
                  : t("admin.saveChanges")}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}